import { db } from '../../../../config/firebaseConfig';
import {
  doc,
  collection,
  getDocs,
  getDoc,
  deleteDoc,
  updateDoc,
  writeBatch,
  query,
} from 'firebase/firestore';

/**
 * Get all users in the system
 * 
 * @returns {Promise<Array>} - List of user objects
 */
export const getAllUsers = async () => {
  try {
    const usersRef = collection(db, 'users');
    const querySnapshot = await getDocs(usersRef);
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    return users;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

/**
 * Delete a user and their data (Admin only)
 * Note: This only deletes Firestore data. Auth deletion requires Admin SDK.
 * 
 * @param {string} userId - ID of the user to delete
 * @returns {Promise<Object>} - Success status
 */
export const deleteUserAsAdmin = async (userId) => {
  try {
    const collectionsToDelete = [
      'draft-portfolios',
      'published-portfolios',
      'images',
      'projects',
      'skills',
      'jobs'
    ];

    for (const collectionName of collectionsToDelete) {
      const colRef = collection(db, 'users', userId, collectionName);
      const querySnapshot = await getDocs(colRef);
      const batch = writeBatch(db);
      querySnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
    }

    await deleteDoc(doc(db, 'users', userId));
    return { success: true, message: `User ${userId} and their data deleted from Firestore` };
  } catch (error) {
    console.error('Error deleting user as admin:', error);
    throw error;
  }
};

/**
 * Grant or revoke admin status for a user
 * 
 * @param {string} userId - ID of the user
 * @param {boolean} isAdmin - Admin status to set
 * @returns {Promise<Object>} - Success status
 */
export const setUserAdminStatus = async (userId, isAdmin) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { isAdmin });
    return { success: true, message: `User ${userId} admin status updated to ${isAdmin}` };
  } catch (error) {
    console.error('Error setting admin status:', error);
    throw error;
  }
};

/**
 * Get all portfolios for a specific user
 * 
 * @param {string} userId - ID of the user
 * @returns {Promise<Object>} - Object containing draft and published portfolios
 */
export const getUserPortfolios = async (userId) => {
  try {
    const publishedRef = collection(db, 'users', userId, 'published-portfolios');

    const [publishedSnap] = await Promise.all([
      getDocs(publishedRef)
    ]);

    const published = publishedSnap.docs.map(doc => ({ id: doc.id, ...doc.data(), type: 'Published' }));

    return published;
  } catch (error) {
    console.error('Error fetching user portfolios:', error);
    throw error;
  }
};

/**
 * Get all projects for a specific user
 * 
 * @param {string} userId - ID of the user
 * @returns {Promise<Array>} - List of project objects
 */
export const getUserProjects = async (userId) => {
  try {
    const projectsRef = collection(db, 'users', userId, 'projects');
    const querySnapshot = await getDocs(projectsRef);
    const projects = [];
    querySnapshot.forEach((doc) => {
      projects.push({ id: doc.id, ...doc.data() });
    });
    return projects;
  } catch (error) {
    console.error('Error fetching user projects:', error);
    throw error;
  }
};

/**
 * Get all skills for a specific user
 * 
 * @param {string} userId - ID of the user
 * @returns {Promise<Array>} - List of skill objects
 */
export const getUserSkills = async (userId) => {
  try {
    const skillsRef = collection(db, 'users', userId, 'skills');
    const querySnapshot = await getDocs(skillsRef);
    const skills = [];
    querySnapshot.forEach((doc) => {
      skills.push({ id: doc.id, ...doc.data() });
    });
    return skills;
  } catch (error) {
    console.error('Error fetching user skills:', error);
    throw error;
  }
};

/**
 * Get platform statistics
 * 
 * @returns {Promise<Object>} - Platform stats
 */
export const getPlatformStats = async () => {
  try {
    const usersRef = collection(db, 'users');
    const usersSnap = await getDocs(usersRef);
    const userCount = usersSnap.size;

    // Count published portfolios across all users
    let totalPublishedPortfolios = 0;
    
    // Also check the shared 'pages' collection
    const pagesRef = collection(db, 'pages');
    const pagesSnap = await getDocs(pagesRef);
    const sharedCount = pagesSnap.size;

    // Iterate through all users to count their published portfolios
    // This might be slow if there are many users
    const publishedPromises = usersSnap.docs.map(userDoc => 
      getDocs(collection(db, 'users', userDoc.id, 'published-portfolios'))
    );
    
    const publishedSnaps = await Promise.all(publishedPromises);
    publishedSnaps.forEach(snap => {
      totalPublishedPortfolios += snap.size;
    });

    return {
      userCount,
      totalPublishedPortfolios,
      sharedCount,
      totalItems: userCount + totalPublishedPortfolios + sharedCount
    };
  } catch (error) {
    console.error('Error fetching platform stats:', error);
    throw error;
  }
};
